import { useEffect, useState } from 'react';
import useRansomwareStore from '../../store/ransomwareStore';

function RansomwarePanel() {
  const { 
    filteredGroups, 
    selectedGroup,
    victims, 
    isLoading, 
    error, 
    currentPage,
    totalPages,
    totalItems,
    searchTerm,
    fetchGroups, 
    fetchVictims, 
    clearSelection,
    setPage,
    setSearchTerm,
    clearSearch 
  } = useRansomwareStore();
  
  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const handleGroupClick = async (groupName) => {
    try {
      await fetchVictims(groupName);
    } catch (err) {
      console.error('Error fetching victims:', err);
    }
  };

  const handleBack = () => {
    clearSelection();
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Search component
  const SearchBar = () => (
    <div className="mb-6">
      <div className="relative">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search groups by name or description..."
          className="w-full pl-10 pr-4 py-2 text-sm text-gray-900 placeholder-gray-500 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        {searchTerm && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            <button
              onClick={clearSearch}
              className="text-gray-400 hover:text-gray-500 focus:outline-none"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
      </div>
      {searchTerm && (
        <p className="mt-2 text-sm text-gray-500">
          Found {totalItems} result{totalItems !== 1 ? 's' : ''} for "{searchTerm}"
        </p>
      )}
    </div>
  );

  // Pagination component
  const Pagination = () => {
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    const maxPageButtons = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
    let endPage = Math.min(startPage + maxPageButtons - 1, totalPages);
    
    if (endPage - startPage + 1 < maxPageButtons) {
      startPage = Math.max(1, endPage - maxPageButtons + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="flex items-center justify-between mt-6">
        <div className="text-sm text-gray-700">
          Showing <span className="font-medium">{Math.min(totalItems, 1 + (currentPage - 1) * 12)}</span> to <span className="font-medium">{Math.min(currentPage * 12, totalItems)}</span> of <span className="font-medium">{totalItems}</span> results
        </div>
        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
          <button
            onClick={() => setPage(currentPage - 1)}
            disabled={currentPage === 1}
            className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
              currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
            }`}
          >
            <span className="sr-only">Previous</span>
            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {pageNumbers.map(number => (
            <button
              key={number}
              onClick={() => setPage(number)}
              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                number === currentPage
                  ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
              }`}
            >
              {number}
            </button>
          ))}

          <button
            onClick={() => setPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
              currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
            }`}
          >
            <span className="sr-only">Next</span>
            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </nav>
      </div>
    );
  };

  const GroupList = () => {
    if (filteredGroups.length === 0 && !isLoading) {
      return (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No groups found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm ? `No results for "${searchTerm}"` : "No ransomware groups are available."}
          </p>
          {searchTerm && (
            <div className="mt-6">
              <button
                onClick={clearSearch}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Clear search
              </button>
            </div>
          )}
        </div>
      );
    }

    return (
      <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredGroups.map((group) => (
            <div 
              key={group.id || group.name} 
              className="bg-white rounded-lg shadow hover:shadow-md transition-shadow duration-200 cursor-pointer h-full"
              onClick={() => handleGroupClick(group.name)}
            >
              <div className="p-4 flex flex-col h-full">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">{group.name}</h3>
                  <div className="mt-2 min-h-[3rem]">
                    {group.description ? (
                      <p className="text-sm text-gray-500 line-clamp-2">{group.description}</p>
                    ) : (
                      <p className="text-sm text-gray-400 italic">No description available</p>
                    )}
                  </div>
                </div>
                
                <div className="mt-auto pt-4 flex justify-between items-center">
                  <div className="flex space-x-2">
                    {group.urls && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {group.urls.length} URL{group.urls.length !== 1 ? 's' : ''}
                      </span>
                    )}
                    {group.profiles && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        {group.profiles.length} Ref{group.profiles.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-gray-500">
                    {formatDate(group.createDate)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <Pagination />
      </>
    );
  };

  const VictimList = () => {
    const [expanded, setExpanded] = useState(false);
    const initialDisplayCount = 10;
    
    if (!victims || victims.length === 0) {
      return (
        <div className="mt-8">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Victims (0)</h3>
          <div className="bg-white shadow overflow-hidden sm:rounded-md p-4">
            <p className="text-sm text-gray-500">No victims found for this group.</p>
          </div>
        </div>
      );
    }

    // Gösterilecek kurban listesi 
    const displayedVictims = expanded ? victims : victims.slice(0, initialDisplayCount);
    const hasMore = victims.length > initialDisplayCount;

    return (
      <div className="mt-8">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Victims ({victims.length})</h3>
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {displayedVictims.map((victim) => (
              <li key={victim.id}>
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-blue-600 truncate">
                      {victim.title}
                    </p>
                    <div className="ml-2 flex-shrink-0 flex">
                      <p className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                        {formatDate(victim.discoverDate)}
                      </p>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
          
          {hasMore && (
            <div className="px-4 py-3 bg-gray-50 text-center border-t border-gray-200">
              <button
                onClick={() => setExpanded(!expanded)}
                className="px-4 py-2 text-sm font-medium text-blue-700 hover:text-blue-900 focus:outline-none"
              >
                {expanded ? "Show less" : `Show all (${victims.length})`}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const GroupDetails = () => {
    if (!selectedGroup) return null;

    return (
      <div className="bg-white rounded-lg shadow p-6 space-y-6">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{selectedGroup.name}</h2>
            <p className="mt-1 text-sm text-gray-500">Created: {formatDate(selectedGroup.createDate)}</p>
          </div>
          <button
            onClick={handleBack}
            className="text-gray-400 hover:text-gray-500"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {selectedGroup.description && (
          <div>
            <h3 className="text-sm font-medium text-gray-500 uppercase">Description</h3>
            <p className="mt-2 text-gray-900">{selectedGroup.description}</p>
          </div>
        )}

        {selectedGroup.urls && selectedGroup.urls.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-gray-500 uppercase">URLs</h3>
            <div className="mt-2 space-y-2">
              {selectedGroup.urls.map((url, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-3">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{url.fqdn}</p>
                      <p className="text-xs text-gray-500">Last Updated: {formatDate(url.updated)}</p>
                    </div>
                    <div className="lg:text-right">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        url.available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {url.available ? 'Available' : 'Unavailable'}
                      </span>
                      {url.title && (
                        <p className="text-xs text-gray-500 mt-1">Title: {url.title}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {selectedGroup.profiles && selectedGroup.profiles.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-gray-500 uppercase">Profiles & References</h3>
            <div className="mt-2 space-y-2">
              {selectedGroup.profiles.map((profile, index) => (
                <a
                  key={index}
                  href={profile}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-sm text-blue-600 hover:text-blue-800 hover:underline"
                >
                  {profile}
                </a>
              ))}
            </div>
          </div>
        )}

        <VictimList />
      </div>
    );
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Ransomware Groups</h1>
        <div className="flex gap-2">
          <button
            onClick={fetchGroups}
            className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-300 rounded-lg hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Refresh
          </button>
        </div>
      </div>

      {!selectedGroup && <SearchBar />}

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
          <h3 className="text-lg font-medium text-gray-900">Loading...</h3>
        </div>
      ) : error ? (
        <div className="text-center text-red-500">
          <p>{error}</p>
        </div>
      ) : selectedGroup ? (
        <GroupDetails />
      ) : (
        <GroupList />
      )}
    </div>
  );
}

export default RansomwarePanel; 